import { useState } from 'preact/hooks';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import styles from '../../styles/AdminFeatures.module.css';
import adminDashboardImage from '../../assets/images/admin-dashboard.png';
import adminScheduleImage from '../../assets/images/admin-schedule3.png';
import adminHoldImage from '../../assets/images/admin_hold.png';
import settlementImage from '../../assets/images/admin-settlement-history.png';
import appVariousProductsImage from '../../assets/images/app_various_product_types.png';
import adminFeedbackTemplateImage from '../../assets/images/admin_feedback_template.png';
import appFeedback1Image from '../../assets/images/app-feedback1.png';
import adminCouponImage from '../../assets/images/admin_coupon_isssue.png';
import appNewUserCouponImage from '../../assets/images/app_new_user_coupon.png';

const features = [
  {
    id: 'dashboard',
    tab: '대시보드',
    title: '대시보드',
    description: '이번 달 매출, 예약 건수, 월별 추이를 한눈에',
    details: [
      '총 매출, 레슨/대관 예약 건수 한눈에 확인',
      '월별 매출 추이 및 결제 수 추이 그래프',
      '상품군별 결제 현황 분석'
    ],
    image: adminDashboardImage
  },
  {
    id: 'schedule',
    tab: '일정 관리',
    title: '일정 관리',
    description: '캘린더 기반 UI로 드래그 앤 드롭으로 손쉽게 일정관리',
    details: [
      '캘린더 기반 드래그 & 드롭 일정 관리',
      '간편한 홀드/보강 처리 및 이력관리',
      '주문 조회에서 이용권 상태 한 번에 확인'
    ],
    image: adminScheduleImage,
    secondaryImage: adminHoldImage
  },
  {
    id: 'settlement',
    tab: '정산',
    title: '정산',
    description: '코치별, 상품별 정산 리포트 제공',
    details: [
      '코치별/상품별 정산 리포트 자동 생성',
      '엑셀 다운로드 후 회계사에게 바로 전달',
      '결제 수단, 정산 사유, 수수료까지 상세 관리'
    ],
    image: settlementImage
  },
  {
    id: 'products',
    tab: '다양한 상품',
    title: '다양한 상품 유형',
    description: '대관, 일일레슨, 정기레슨, 횟수권 등 다양한 형태의 상품 운영',
    details: [
      '대관, 일일레슨, 정기레슨, 횟수권 지원',
      '정기 레슨도 상담 없이 앱에서 바로 예약, 결제',
      '다이빙 과정처럼 여러 회차 구성 상품도 가능'
    ],
    image: appVariousProductsImage
  },
  {
    id: 'feedback',
    tab: '레슨 피드백',
    title: '레슨 피드백',
    description: '수업 회차별 피드백 작성 기능',
    details: [
      '자유 형식 또는 피드백 템플릿으로 작성',
      '칭찬할 점, 보완할 점, 숙제, 레슨 사진 기록',
      '고객은 앱에서 피드백 알림 및 내용 확인'
    ],
    images: [
      { src: adminFeedbackTemplateImage, alt: '피드백 템플릿', type: 'admin' },
      { src: appFeedback1Image, alt: '앱 피드백 화면', type: 'app' }
    ]
  },
  {
    id: 'marketing',
    tab: '마케팅 도구',
    title: '마케팅 도구',
    description: '쇼핑몰에서 쓰는 마케팅 무기들을 우리 센터에서도 활용',
    details: [
      '신규 고객 할인 쿠폰으로 체험 레슨 유입 유도',
      '유휴 시간대 할인으로 비인기 시간대 예약률 증가',
      '재등록 할인 쿠폰 및 자동 만료 알림'
    ],
    images: [
      { src: adminCouponImage, alt: '쿠폰 발급', type: 'admin' },
      { src: appNewUserCouponImage, alt: '신규 고객 쿠폰', type: 'app' }
    ]
  }
];

export default function AdminFeatures() {
  const [ref, isVisible] = useScrollAnimation({ once: true });
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="admin-features" ref={ref} className={`section ${styles.adminFeatures}`}>
      <div className="section-content">
        <h2 className={`text-center ${styles.title}`}>
          올림이 제공하는 <span className={styles.highlight}>핵심 기능</span>
        </h2>
        <p className={`text-center ${styles.subtitle}`}>
          버튼을 눌러 각 기능을 확인해보세요
        </p>

        <div className={`${styles.container} ${isVisible ? styles.visible : ''}`}>
          {/* 탭 메뉴 - 가로 배열 */}
          <div className={styles.tabs}>
            {features.map((feature, index) => (
              <button
                key={feature.id}
                className={`${styles.tab} ${activeTab === index ? styles.active : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {feature.tab}
              </button>
            ))}
          </div>

          {/* 탭 콘텐츠 */}
          <div className={styles.content}>
            <div className={styles.textContent}>
              <h3 className={styles.featureTitle}>{features[activeTab].title}</h3>
              <p className={styles.featureDesc}>{features[activeTab].description}</p>
              <ul className={styles.details}>
                {features[activeTab].details.map((detail, index) => (
                  <li key={index}>
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.imageContent}>
              {features[activeTab].images ? (
                <div className={styles.multiImageGrid}>
                  {features[activeTab].images.map((img, index) => (
                    <div
                      key={index}
                      className={`${styles.gridImage} ${img.type === 'app' ? styles.appImage : styles.adminImage}`}
                    >
                      <img src={img.src} alt={img.alt} />
                    </div>
                  ))}
                </div>
              ) : (
                <img
                  src={features[activeTab].image}
                  alt={features[activeTab].tab}
                  className={styles.featureImage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
